import { ApprovalsApiService } from '@api/approvals'
import {
  type MachineAttachmentItemDto,
  type MachineQuotationItemUpdateDto,
  MachinesApiService,
  type QuotationSellPriceDto,
  type TradeInItemDto
} from '@api/machines'
import {
  GENERAL_MACHINE_TYPES,
  handleCatchError,
  MACHINE_TYPE,
  StockTypes
} from '@common/utils'
import { successToast } from '@components/reusable'
import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery
} from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'
import { useQuoteData } from '@/pages/quotation-tool/hooks/useQuoteData'
import { useQuote } from '@/pages/quotation-tool/stores/quoteStore'
import { EQUIPMENT_TYPE } from '@/pages/quotation-tool/utils/constants'
import { EQUIPMENTS_QUERY_KEYS } from '@/shared-queries/machines'
import { EQUIPMENT_QUERY_KEYS } from './equipmentQueries'

export const QUOTE_QUERY_KEYS = {
  QUOTE: 'quote',
  QUOTE_APPROVERS: 'quoteApprovers',
  QUOTE_SPECS_BY_CUSTOMER: 'quoteSpecsByCustomer',
  QUOTE_SPEC_BY_SPEC_ID: 'quoteSpecBySpecId',
  QUOTE_ARCHIVED_EQUIPMENTS: 'quoteArchivedEquipments',
  QUOTE_TRADE_IN_TYPES: 'quoteTradeInTypes',
  QUOTE_MANUFACTURER_LIST: 'quoteManufacturerList'
}

export const useGetQuote = (quoteId?: string) => {
  return useSuspenseQuery({
    queryKey: [QUOTE_QUERY_KEYS.QUOTE, quoteId],
    queryFn: async () => {
      try {
        if (quoteId) {
          return await MachinesApiService.quotation(quoteId)
        }
      } catch (error) {
        handleCatchError(error)
      }
    },
    staleTime: 0,
    gcTime: 30 * 60 * 1000, // 30 minutes
    refetchOnMount: 'always'
  })
}

export const useGetTradeInTypes = (isReadOnly: boolean) => {
  return useQuery({
    queryKey: [QUOTE_QUERY_KEYS.QUOTE_TRADE_IN_TYPES, isReadOnly],
    queryFn: async () => {
      try {
        return await MachinesApiService.quotationTradeInTypes()
      } catch (error) {
        handleCatchError(error)
      }
    },
    enabled: !isReadOnly
  })
}

export const useGetTradeInManufacturerList = (isReadOnly: boolean) => {
  return useQuery({
    queryKey: [QUOTE_QUERY_KEYS.QUOTE_MANUFACTURER_LIST, isReadOnly],
    queryFn: async () => {
      try {
        return await MachinesApiService.tradeInAllManufacturers()
      } catch (error) {
        handleCatchError(error)
      }
    },
    enabled: !isReadOnly
  })
}

export const useAddNewManufacterer = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (manufacturerName: string) => {
      return await MachinesApiService.tradeInAddManufacturer({
        name: manufacturerName
      })
    },
    throwOnError: false,
    onError: error => handleCatchError(error),
    onSuccess: () => {
      successToast()
      queryClient.invalidateQueries({
        queryKey: [QUOTE_QUERY_KEYS.QUOTE_MANUFACTURER_LIST]
      })
    }
  })
}

export const useChangeManchineAttachmentQty = (quoteId?: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      attachment,
      machineId,
      action
    }: {
      action: 'add' | 'subtract'
      machineId: string
      attachment: MachineAttachmentItemDto
    }) => {
      const qty =
        action === 'add'
          ? (attachment.quantity ?? 0) + 1
          : (attachment.quantity ?? 0) - 1

      if (quoteId) {
        return await MachinesApiService.quotationMachineMachineAttachmentUpdateQuantity(
          quoteId,
          machineId,
          attachment?.quotationItemId as string,
          qty
        )
      }
    },
    throwOnError: false,
    onError: error => handleCatchError(error),
    onSuccess: () => {
      successToast()
      queryClient.invalidateQueries({
        queryKey: [QUOTE_QUERY_KEYS.QUOTE, quoteId]
      })
    }
  })
}

export const useDeleteMachineAttachment = ({
  quoteId,
  isEditEq
}: {
  quoteId?: string
  isEditEq: boolean
}) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      attachmentItemId,
      machineItemId
    }: {
      machineItemId: string
      attachmentItemId: string
    }) => {
      if (quoteId) {
        return await MachinesApiService.quotationMachineRemoveMachineAttachment(
          quoteId,
          machineItemId,
          attachmentItemId
        )
      }
    },
    throwOnError: false,
    onError: error => handleCatchError(error),
    onSuccess: async (response, varaibles) => {
      successToast()

      if (isEditEq) {
        const patchDraft = (oldData?: MachineQuotationItemUpdateDto) => ({
          ...oldData,
          machineQuotationItem: response?.machineQuotationItem,
          machineItemPriceOld: response?.machineItemPriceOld ?? {}
        })

        queryClient.setQueryData<MachineQuotationItemUpdateDto>(
          [
            EQUIPMENT_QUERY_KEYS.GET_EQUIPMENT_CACHE,
            quoteId,
            varaibles.machineItemId
          ],
          patchDraft
        )

        queryClient.setQueryData<MachineQuotationItemUpdateDto>(
          [
            EQUIPMENTS_QUERY_KEYS.GET_DRAFT_EQUIPMENT,
            quoteId,
            varaibles.machineItemId
          ],
          patchDraft
        )
      } else {
        const waitForBackendPropagation = () =>
          new Promise(resolve => setTimeout(resolve, 4500))

        await waitForBackendPropagation()

        queryClient.invalidateQueries({
          queryKey: [QUOTE_QUERY_KEYS.QUOTE, quoteId]
        })
      }
    }
  })
}

export const useGetQuoteApprovers = () => {
  const { quoteId } = useParams()
  const [searchParams] = useSearchParams()
  const currentTab = searchParams.get('tab')

  const enableApiCall = currentTab === 'dealSummary' && quoteId

  return useSuspenseQuery({
    // enableApiCall is part of the key so switching to the deal summary tab
    // refetches instead of serving the stale null cached from other tabs (bug 37357)
    queryKey: [
      QUOTE_QUERY_KEYS.QUOTE_APPROVERS,
      quoteId,
      Boolean(enableApiCall)
    ],
    queryFn: async () => {
      if (enableApiCall) {
        try {
          return await ApprovalsApiService.getApproversLevelsData(quoteId)
        } catch (error) {
          handleCatchError(error)
        }
      }
      return null
    },
    refetchOnMount: true,
    staleTime: 2 * 60 * 1000 // 2 minutes
  })
}

export const useGetSpecsByCustomer = (customerId?: number) => {
  return useSuspenseQuery({
    queryKey: [QUOTE_QUERY_KEYS.QUOTE_SPECS_BY_CUSTOMER, customerId],
    queryFn: async () => {
      try {
        if (!customerId) {
          return null
        }

        return await MachinesApiService.customersGetAllSpecsByCustomer(
          customerId
        )
      } catch (error) {
        handleCatchError(error)
      }
    }
  })
}

export const useGetSpecBySpecId = () => {
  const { specId } = useParams()
  const {
    customer: { id: customerId }
  } = useQuoteData()

  return useSuspenseQuery({
    queryKey: [QUOTE_QUERY_KEYS.QUOTE_SPEC_BY_SPEC_ID, specId, customerId],
    queryFn: async () => {
      try {
        if (!(specId && customerId)) {
          return null
        }

        return await MachinesApiService.specSpec(specId, Number(customerId))
      } catch (error) {
        handleCatchError(error)
      }
    },
    staleTime: 0,
    refetchOnMount: 'always'
  })
}

export const useGetArchivedEquipmentsFromSpec = (customerId: number) => {
  return useMutation({
    mutationFn: async (specId: string) => {
      return await MachinesApiService.specsSpecsWithArchivedEquipment(
        specId,
        customerId
      )
    },
    throwOnError: false,
    onError: error => handleCatchError(error)
  })
}

export const useReturnQuoteToDraft = () => {
  const queryClient = useQueryClient()
  const { t } = useTranslation('', {
    keyPrefix: 'newQuote.status.returnToDraft'
  })

  return useMutation({
    mutationFn: async (quoteId: string) => {
      return await ApprovalsApiService.returnToDraft({
        quotationId: quoteId
      })
    },
    throwOnError: false,
    onError: error => handleCatchError(error),
    onSuccess(_, variables, __) {
      successToast(t('successMessage'))
      queryClient.invalidateQueries({
        queryKey: [QUOTE_QUERY_KEYS.QUOTE, variables]
      })
    }
  })
}

export const useUpdateSpecName = ({
  specId,
  customerId
}: {
  specId: string
  customerId: number
}) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (newSpecName: string) => {
      return await MachinesApiService.customersUpdateSpecName(
        customerId,
        specId,
        newSpecName
      )
    },
    throwOnError: false,
    onError: error => handleCatchError(error),
    onSuccess() {
      successToast()
      queryClient.invalidateQueries({
        queryKey: [QUOTE_QUERY_KEYS.QUOTE_SPECS_BY_CUSTOMER, customerId]
      })
    }
  })
}

export const useUpdateSpecNote = ({
  specId,
  customerId
}: {
  specId: string
  customerId: number
}) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (note: string) => {
      return await MachinesApiService.customersUpdateSpecNote(
        customerId,
        specId,
        note
      )
    },
    throwOnError: false,
    onError: error => handleCatchError(error),
    onSuccess() {
      successToast()
      queryClient.invalidateQueries({
        queryKey: [QUOTE_QUERY_KEYS.QUOTE_SPEC_BY_SPEC_ID, specId]
      })
    }
  })
}

export const useAddSpecToQuotation = ({
  quoteId,
  customerId
}: {
  quoteId?: string | null
  customerId: number
}) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (specId: string) => {
      if (quoteId) {
        return await MachinesApiService.specAddSpecToQuotation(
          specId,
          customerId,
          quoteId
        )
      }
    },
    throwOnError: false,
    onError: error => {
      handleCatchError(error)
    },
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: [QUOTE_QUERY_KEYS.QUOTE, quoteId]
      })
    }
  })
}

export const useCopyEquipments = ({ quoteId }: { quoteId?: string }) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      customerId,
      eqType,
      items,
      specId,
      templateId
    }: {
      specId: string
      templateId: string
      customerId: number
      items: number[]
      eqType: string
    }) => {
      const fnCall =
        eqType === EQUIPMENT_TYPE.MACHINE
          ? MachinesApiService.specAddMachinesByTemplateToQuotation
          : MachinesApiService.specAddAttachmentsByTemplateToQuotation

      if (quoteId) {
        return await fnCall(specId, templateId, customerId, quoteId, items)
      }
    },
    throwOnError: false,
    onError: error => handleCatchError(error),
    onSuccess(_, variables, __) {
      successToast()
      queryClient.invalidateQueries({
        queryKey: [QUOTE_QUERY_KEYS.QUOTE, variables]
      })
    }
  })
}

export const useUpdateQuoteDetails = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      quoteId,
      newQuoteName,
      customerId,
      customerContactId,
      deliveryAddressId,
      billingAddressId,
      quoteOwner
    }: {
      quoteId: string
      newQuoteName: string
      customerId?: string
      customerContactId: number
      deliveryAddressId: number
      billingAddressId?: number
      quoteOwner?: string
    }) => {
      const response = await MachinesApiService.quotationUpdateDetails(
        quoteId,
        newQuoteName,
        customerId,
        customerContactId,
        deliveryAddressId,
        billingAddressId,
        quoteOwner
      )
      if (response) {
        successToast()
      }
      return response
    },
    throwOnError: false,
    onError: error => handleCatchError(error),
    onSuccess(_, variables, __) {
      successToast()
      queryClient.invalidateQueries({
        queryKey: [QUOTE_QUERY_KEYS.QUOTE, variables.quoteId]
      })
    }
  })
}

export const useUpdateLockProposedPrice = (quoteId?: string) => {
  const { setSkipQuoteSocket } = useQuote()

  return useMutation({
    mutationFn: async (isPriceLocked: boolean) => {
      setSkipQuoteSocket(true)
      return await MachinesApiService.quotationUpdatePriceLock(
        quoteId ?? '',
        isPriceLocked
      )
    },
    throwOnError: false,
    onError: error => {
      setSkipQuoteSocket(false)
      handleCatchError(error)
    },
    onSuccess() {
      setTimeout(() => {
        setSkipQuoteSocket(false)
      }, 1000)
    }
  })
}

export const useUpdateQuotePrices = (
  quoteId?: string,
  disableMessage = false
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      body,
      introToFinance
    }: {
      body: QuotationSellPriceDto
      introToFinance?: boolean
    }) => {
      body.machineItems = body.machineItems?.map(item => ({
        ...item,
        machineAttachments: item.machineAttachments?.map(attachment => ({
          ...attachment,
          quantity: 1
        }))
      }))

      return await MachinesApiService.quotationUpdatePrices(
        body,
        introToFinance
      )
    },
    throwOnError: false,
    onError: error => handleCatchError(error),
    onSuccess() {
      if (!disableMessage) {
        successToast()
      }

      queryClient.invalidateQueries({
        queryKey: [QUOTE_QUERY_KEYS.QUOTE, quoteId]
      })
    }
  })
}

export const useUpdateQuantity = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      quotationId,
      machineId,
      qty,
      machineType
    }: {
      quotationId: string
      machineId: string
      qty: number
      machineType: string
    }) => {
      const updateQtyEndpointByMachineType =
        machineType === GENERAL_MACHINE_TYPES.MACHINE
          ? MachinesApiService.quotationMachineUpdateQuantity
          : MachinesApiService.quotationMachineAttachmentUpdateQuantity

      return await updateQtyEndpointByMachineType(quotationId, machineId, qty)
    },
    throwOnError: false,
    onError: error => handleCatchError(error),
    onSuccess: (_, variables, __) => {
      successToast()

      queryClient.invalidateQueries({
        queryKey: [QUOTE_QUERY_KEYS.QUOTE, variables.quotationId]
      })
    }
  })
}

export const useUpdateQuoteNotes = () => {
  return useMutation({
    mutationFn: async ({
      quoteId,
      customerNotes
    }: {
      quoteId: string
      customerNotes: string
    }) => {
      await MachinesApiService.quotationUpdateNotes(quoteId, customerNotes)
    },
    throwOnError: false,
    onError: error => {
      handleCatchError(error)
    },
    onSuccess: () => {
      successToast()
    }
  })
}

export const useDeleteItemFromQuotationByMachineType = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      quotationId,
      quotationItemId,
      machineType
    }: {
      quotationId: string
      quotationItemId: string
      machineType: string
    }) => {
      const deleteEndpointByMachineType =
        machineType === GENERAL_MACHINE_TYPES.MACHINE
          ? MachinesApiService.quotationDeleteMachine
          : MachinesApiService.quotationDeleteMachineAttachment

      return await deleteEndpointByMachineType(quotationId, quotationItemId)
    },
    throwOnError: false,
    onError: error => handleCatchError(error),
    onSuccess: (_, variables, __) => {
      successToast()
      queryClient.invalidateQueries({
        queryKey: [QUOTE_QUERY_KEYS.QUOTE, variables.quotationId]
      })
    }
  })
}

export const useUpdateCrdByMachineType = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      quoteId,
      quotationItemId,
      crd,
      machineType
    }: {
      quoteId: string
      quotationItemId: string
      crd: string
      machineType: string
    }) => {
      const updateEndpointByMachineType =
        machineType === GENERAL_MACHINE_TYPES.MACHINE
          ? MachinesApiService.quotationMachineUpdateCrd
          : MachinesApiService.quotationMachineAttachmentUpdateCrd

      return await updateEndpointByMachineType(quoteId, quotationItemId, crd)
    },
    throwOnError: false,
    onError: error => handleCatchError(error),
    onSuccess: (_, variables) => {
      successToast()
      queryClient.invalidateQueries({
        queryKey: [QUOTE_QUERY_KEYS.QUOTE, variables.quoteId]
      })
    }
  })
}

export const useOnAddMachineToQuotation = ({
  quotationId
}: {
  quotationId?: string
}) => {
  return useMutation({
    mutationFn: async ({
      equipmentId,
      machineType,
      stockType
    }: {
      equipmentId: number
      machineType: string
      stockType?: string
    }) => {
      if (quotationId === undefined) {
        return null
      }

      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      let addEndpointByMachineType: any

      if (machineType === MACHINE_TYPE.MACHINE) {
        if (
          stockType === StockTypes.IN_STOCK ||
          stockType === StockTypes.ON_ORDER
        ) {
          addEndpointByMachineType = MachinesApiService.quotationAddStockItem
        } else {
          addEndpointByMachineType = MachinesApiService.quotationAddMachine
        }
      }

      if (machineType === MACHINE_TYPE.ATTACHMENT) {
        if (
          stockType === StockTypes.IN_STOCK ||
          stockType === StockTypes.ON_ORDER
        ) {
          addEndpointByMachineType = MachinesApiService.quotationAddStockItem
        } else {
          addEndpointByMachineType =
            MachinesApiService.quotationAddMachineAttachment
        }
      }

      return await addEndpointByMachineType(quotationId, equipmentId)
    },
    throwOnError: false,
    onError: error => handleCatchError(error),
    onSuccess: data => {
      if (data) {
        successToast()
      }
    }
  })
}

export const useRearrangeQuoteItems = () => {
  return useMutation({
    mutationFn: async ({
      quotationId,
      itemId,
      toIndex
    }: {
      quotationId: string
      itemId: string
      toIndex: number
    }) => {
      if (quotationId) {
        return await MachinesApiService.quotationReorderItem(
          quotationId,
          itemId,
          toIndex
        )
      }
    },
    throwOnError: false,
    onError: error => handleCatchError(error),
    onSuccess: () => {
      successToast()
    }
  })
}

export const useOnAddAttachmentWithinMachineToQuotation = ({
  quotationId
}: {
  quotationId?: string
}) => {
  return useMutation({
    mutationFn: async ({
      machineId,
      selectedAttachmentId,
      stockType
    }: {
      machineId: string
      selectedAttachmentId: number
      stockType: string
    }) => {
      if (quotationId === undefined) {
        return null
      }

      const addEndpointByStockType =
        stockType === StockTypes.FACTORY_ORDER
          ? MachinesApiService.quotationMachineAddMachineAttachment
          : MachinesApiService.quotationMachineAddMachineAttachmentStock

      return await addEndpointByStockType(
        quotationId,
        machineId,
        selectedAttachmentId
      )
    },
    throwOnError: false,
    onError: error => handleCatchError(error),
    onSuccess: () => {
      successToast()
    }
  })
}

export const useAddTradeIn = ({ quotationId }: { quotationId?: string }) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: TradeInItemDto) => {
      if (quotationId) {
        return await MachinesApiService.quotationAddTradeIn(quotationId, data)
      }
    },
    throwOnError: false,
    onError: error => handleCatchError(error),
    onSuccess: () => {
      successToast()
      queryClient.invalidateQueries({
        queryKey: [QUOTE_QUERY_KEYS.QUOTE, quotationId]
      })
    }
  })
}

export const useEditTradeIn = ({ quotationId }: { quotationId?: string }) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: TradeInItemDto) => {
      if (quotationId && data?.quotationItemId) {
        return await MachinesApiService.quotationUpdateTradeIn(
          quotationId,
          data.quotationItemId,
          data
        )
      }
    },
    throwOnError: false,
    onError: error => handleCatchError(error),
    onSuccess: () => {
      successToast()
      queryClient.invalidateQueries({
        queryKey: [QUOTE_QUERY_KEYS.QUOTE, quotationId]
      })
    }
  })
}

export const useDeleteTradeIn = ({ quotationId }: { quotationId?: string }) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (tradeInId: string) => {
      if (quotationId) {
        return await MachinesApiService.quotationDeleteTradeIn(
          quotationId,
          tradeInId
        )
      }
    },
    throwOnError: false,
    onError: error => handleCatchError(error),
    onSuccess: () => {
      successToast()
      queryClient.invalidateQueries({
        queryKey: [QUOTE_QUERY_KEYS.QUOTE, quotationId]
      })
    }
  })
}

export const useMoveQuoteFromAccepted = ({ quoteId }: { quoteId?: string }) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      if (quoteId) {
        return await MachinesApiService.quotationReturnToDraft([quoteId])
      }
    },
    throwOnError: false,
    onError: error => handleCatchError(error),
    onSuccess: () => {
      successToast()
      queryClient.invalidateQueries({
        queryKey: [QUOTE_QUERY_KEYS.QUOTE, quoteId]
      })
    }
  })
}
